import React, { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
// eslint-disable-next-line @typescript-eslint/no-var-requires
import MaplibreGeocoder from '@maplibre/maplibre-gl-geocoder';
import '@maplibre/maplibre-gl-geocoder/dist/maplibre-gl-geocoder.css';
import { Box, Typography, Button } from '@mui/material';
import { C } from '../theme';
import { isWebglSupported } from './mapSupport';
import './map.css';

interface LatLng { lat: number; lng: number; }
interface LocationPickerProps {
  value: LatLng | null;
  onChange: (coords: LatLng | null) => void;
  height?: number;
}

// Free dark basemap (no API key) + free Photon geocoding, biased to the Balkans.
const DARK_STYLE = 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json';
const BELGRADE: [number, number] = [20.4573, 44.8176];
const GOLD = '#F3C015';
const SEARCH_DEBOUNCE_MS = 300;

// Photon query, adapted to the format the maplibre geocoder control expects.
const fetchPhoton = async (query: string) => {
  const features: any[] = [];
  try {
    const url =
      `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}` +
      `&limit=6&lang=default&lat=${BELGRADE[1]}&lon=${BELGRADE[0]}`;
    const res = await fetch(url);
    const geojson = await res.json();
    for (const f of geojson.features || []) {
      const [lng, lat] = f.geometry.coordinates;
      const p = f.properties || {};
      const name = [p.name, p.street, p.city, p.state, p.country].filter(Boolean).join(', ');
      features.push({
        type: 'Feature',
        id: String(p.osm_id || `${lng},${lat}`),
        place_name: name,
        place_type: ['place'],
        text: p.name || name,
        properties: p,
        center: [lng, lat],
        geometry: { type: 'Point', coordinates: [lng, lat] },
      });
    }
  } catch (e) {
    // network/geocoder failure -> empty results; picker still usable by clicking the map
  }
  return features;
};

// Debounced geocoder: only the last keystroke within 300ms actually hits Photon,
// so type-ahead search can't hammer the public endpoint.
const makePhotonGeocoder = () => {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pendingResolve: ((v: any) => void) | null = null;
  return {
    forwardGeocode: (config: any) =>
      new Promise((resolve) => {
        if (timer) {
          clearTimeout(timer);
          if (pendingResolve) pendingResolve({ features: [] }); // free the superseded request
        }
        pendingResolve = resolve;
        timer = setTimeout(async () => {
          const features = await fetchPhoton(config.query);
          resolve({ features });
          pendingResolve = null;
          timer = null;
        }, SEARCH_DEBOUNCE_MS);
      }),
  };
};

const LocationPicker: React.FC<LocationPickerProps> = ({ value, onChange, height = 320 }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const markerRef = useRef<maplibregl.Marker | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const initialRef = useRef(value);
  const [supported] = useState(isWebglSupported);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!supported || !containerRef.current) return;

    let map: maplibregl.Map;
    try {
      map = new maplibregl.Map({
        container: containerRef.current,
        style: DARK_STYLE,
        center: initialRef.current ? [initialRef.current.lng, initialRef.current.lat] : BELGRADE,
        zoom: initialRef.current ? 14 : 11,
        attributionControl: { compact: true },
      });
    } catch (e) {
      setFailed(true);
      return;
    }
    map.on('error', () => {});
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');

    const placeMarker = (lng: number, lat: number, emit = true) => {
      if (!markerRef.current) {
        markerRef.current = new maplibregl.Marker({ color: GOLD, draggable: true })
          .setLngLat([lng, lat])
          .addTo(map);
        markerRef.current.on('dragend', () => {
          const ll = markerRef.current!.getLngLat();
          onChangeRef.current({ lat: +ll.lat.toFixed(6), lng: +ll.lng.toFixed(6) });
        });
      } else {
        markerRef.current.setLngLat([lng, lat]);
      }
      if (emit) onChangeRef.current({ lat: +lat.toFixed(6), lng: +lng.toFixed(6) });
    };

    if (initialRef.current) placeMarker(initialRef.current.lng, initialRef.current.lat, false);

    map.on('click', (e) => placeMarker(e.lngLat.lng, e.lngLat.lat));

    let geocoder: any = null;
    try {
      geocoder = new MaplibreGeocoder(makePhotonGeocoder(), {
        maplibregl,
        marker: false,
        showResultsWhileTyping: true,
        placeholder: 'Pretraži adresu ili ulicu…',
        collapsed: false,
      });
      map.addControl(geocoder, 'top-left');
      geocoder.on('result', (e: any) => {
        const [lng, lat] = e.result.center;
        placeMarker(lng, lat);
        map.flyTo({ center: [lng, lat], zoom: 15 });
      });
    } catch (e) {
      // geocoder failed to mount -> map still works by clicking
    }

    return () => {
      markerRef.current = null;
      map.remove();
    };
  }, [supported]);

  const clear = () => {
    markerRef.current?.remove();
    markerRef.current = null;
    onChangeRef.current(null);
  };

  // Graceful fallback when WebGL is unavailable or the map fails to initialise.
  if (!supported || failed) {
    return (
      <Box
        sx={{
          height,
          borderRadius: 2,
          border: `1px solid ${C.line}`,
          backgroundColor: C.surface2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          px: 3,
        }}
      >
        <Typography variant="body2" sx={{ color: C.muted, maxWidth: 360 }}>
          Interaktivna mapa nije podržana u vašem pregledaču. Možete nastaviti bez lokacije ili je
          dodati kasnije iz drugog pregledača.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        ref={containerRef}
        className="ni-map"
        sx={{ height, width: '100%', borderRadius: 2, overflow: 'hidden', border: `1px solid ${C.line}` }}
      />
      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Typography variant="body2" sx={{ color: C.muted }}>
          {value
            ? `Pin: ${value.lat.toFixed(5)}, ${value.lng.toFixed(5)}`
            : 'Pretražite adresu ili kliknite na mapu da postavite lokaciju (opciono).'}
        </Typography>
        {value && (
          <Button size="small" onClick={clear} sx={{ color: C.muted, flexShrink: 0 }}>
            Ukloni lokaciju
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default LocationPicker;
